import React from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Msg #990 — Admin: list all admin-created custom NPCs, edit or delete each.
export default function ManageNpcsModal({ open, onClose, requester, onEdit }) {
  const [npcs, setNpcs] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [note, setNote] = React.useState('');

  const load = React.useCallback(async () => {
    setErr('');
    try {
      const r = await axios.get(`${API}/admin/npc/list`, { params: { requester } });
      setNpcs(r.data?.npcs || []);
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Failed to load custom NPCs.');
    }
  }, [requester]);
  React.useEffect(() => { if (open) { setNote(''); load(); } }, [open, load]);
  if (!open) return null;

  const del = async (nm) => {
    if (!window.confirm(`Delete custom NPC "${nm}"? This cannot be undone.`)) return;
    setBusy(true); setErr(''); setNote('');
    try {
      await axios.delete(`${API}/admin/npc/${encodeURIComponent(nm)}`, {
        params: { requester },
      });
      setNote(`Deleted ${nm}.`);
      await load();
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Delete failed.');
    } finally { setBusy(false); }
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 1200,
    background: 'rgba(20,40,20,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const shell = {
    width: 'min(96vw, 720px)', maxHeight: '86vh',
    display: 'flex', flexDirection: 'column',
    background: '#f5e5c0', border: '2px solid #7a5a1a',
    fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#3a2b14',
  };

  return (
    <div style={overlay} data-testid="manage-npcs-modal" onClick={onClose}>
      <div style={shell} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', background: '#4a3a1a', color: '#ffe1a0',
          borderBottom: '1px solid #7a5a1a',
        }}>
          <div style={{ fontWeight: 'bold', letterSpacing: 2, textTransform: 'uppercase', fontSize: 14 }}>
            🐾 Manage Custom NPCs
          </div>
          <button
            onClick={onClose}
            data-testid="manage-npcs-close"
            style={{ background: 'transparent', color: '#ffe1a0',
                     border: '1px solid #ffe1a0', padding: '2px 10px',
                     fontWeight: 'bold', cursor: 'pointer' }}
          >✕</button>
        </div>
        {(err || note) && (
          <div style={{ padding: '4px 14px', fontSize: 12 }}>
            {err && <span data-testid="manage-npcs-error" style={{ color: '#7a1f1f' }}>{err}</span>}
            {note && <span data-testid="manage-npcs-note" style={{ color: '#1c5a1c' }}>{note}</span>}
          </div>
        )}
        <div style={{
          padding: '6px 14px', fontSize: 11, opacity: 0.8,
          borderBottom: '1px dashed #a08a30',
        }}>
          {npcs.length} custom {npcs.length === 1 ? 'NPC' : 'NPCs'} · auto-loaded from db.custom_npcs.
        </div>
        <div
          data-testid="manage-npcs-list"
          style={{ flex: 1, overflowY: 'auto', background: '#fdf7e0' }}
        >
          {npcs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', opacity: 0.7, fontSize: 12 }}>
              No custom NPCs yet — use “🐣 CREATE NPC” to add one.
            </div>
          ) : npcs.map((n) => (
            <div
              key={n.name_lc}
              data-testid={`manage-npcs-row-${n.name_lc}`}
              style={{
                padding: '8px 14px',
                borderBottom: '1px dashed #d0b880',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <span
                style={{
                  minWidth: 12, height: 12, background: n.color || '#5a3a8a',
                  border: '1px solid #3a2b14', display: 'inline-block',
                }}
                title={n.color}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', color: n.color || '#3a2b14' }}>{n.name}</div>
                <div style={{ fontSize: 10, opacity: 0.75 }}>
                  <b>{n.npc_type}</b>
                  {n.gender && n.gender !== 'undisclosed' && <> · {n.gender}</>}
                  {n.is_female && <> · ♥ hearts</>}
                  {n.hp_max && <> · HP {n.hp_max}</>}
                  {' · rooms: '}{(n.rooms || []).join(', ') || '(default)'}
                </div>
                <div style={{ fontSize: 10, opacity: 0.65, marginTop: 2 }}>
                  {(n.description || '(no description)').slice(0, 140)}
                </div>
              </div>
              <button
                data-testid={`manage-npcs-edit-${n.name_lc}`}
                onClick={() => { if (onEdit) onEdit(n); }}
                disabled={busy}
                style={{
                  background: '#4a3a1a', color: '#ffe1a0',
                  border: '1px solid #a08a30', padding: '3px 10px',
                  fontSize: 10, fontWeight: 'bold', cursor: 'pointer',
                }}
              >EDIT</button>
              <button
                data-testid={`manage-npcs-delete-${n.name_lc}`}
                onClick={() => del(n.name)}
                disabled={busy}
                style={{
                  background: '#7a1f1f', color: '#fce2d0',
                  border: '1px solid #3a0e0e', padding: '3px 10px',
                  fontSize: 10, fontWeight: 'bold', cursor: 'pointer',
                }}
              >DELETE</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
